import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { scoreColor, scoreTextColor } from './InternDashboard';
import { Grid3x3 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const textFor = (v) => {
  if (v == null) return '#c0c0c0';
  const i = Math.round(v);
  return i <= 2 ? '#ffffff' : '#1A1A1A';
};

export const SkillMasteryModal = ({ open, onOpenChange, archived }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [program, setProgram] = useState('all');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const params = { archived };
    if (program !== 'all') params.program_id = program;
    axios.get(`${API}/dashboard/skill-mastery`, { params })
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, archived, program]);

  const skills = data?.skills || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl max-h-[88vh] overflow-auto" data-testid="skill-mastery-modal">
        <DialogHeader>
          <DialogTitle className="font-['Manrope'] flex items-center gap-2">
            <Grid3x3 className="w-5 h-5 text-[#34657f]" /> Skill Mastery
          </DialogTitle>
          <DialogDescription>
            Average rubric level (0–6) per soft skill across interns. Darker green = stronger mastery; red = needs support.
          </DialogDescription>
        </DialogHeader>

        {(data?.programs?.length > 1) && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-[#5C5C5C]">Class / program:</span>
            <select value={program} onChange={(e) => setProgram(e.target.value)}
              className="border border-[#E5E0D8] rounded-lg px-2 py-1 text-sm" data-testid="skill-mastery-program">
              <option value="all">All programs</option>
              {data.programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        )}

        {loading && <p className="text-center text-[#5C5C5C] py-10">Loading…</p>}

        {!loading && data && (
          data.interns.length === 0 ? (
            <p className="text-center text-[#8A8A8A] py-10">No interns in this view.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm" data-testid="skill-mastery-table">
                <thead>
                  <tr>
                    <th className="text-left sticky left-0 bg-white p-2 font-semibold text-[#1A1A1A] min-w-[160px]">Intern</th>
                    {skills.map((s) => (
                      <th key={s.key} className="p-1 text-[11px] font-semibold text-[#5C5C5C] align-bottom" style={{ minWidth: 74 }}>
                        <div className="leading-tight">{s.label}</div>
                      </th>
                    ))}
                    <th className="p-1 text-[11px] font-semibold text-[#5C5C5C]">Overall</th>
                  </tr>
                </thead>
                <tbody>
                  {data.interns.map((r) => (
                    <tr key={r.id} className="border-t border-[#F0EDE7]" data-testid={`skill-row-${r.id}`}>
                      <td className="sticky left-0 bg-white p-2">
                        <div className="font-medium text-[#1A1A1A]">{r.name}</div>
                        <div className="text-[11px] text-[#8A8A8A]">{r.program_name} · {r.report_count} report{r.report_count === 1 ? '' : 's'}</div>
                      </td>
                      {skills.map((s) => {
                        const v = r.skills[s.key];
                        return (
                          <td key={s.key} className="p-1 text-center">
                            <div className="rounded-md py-2 font-semibold"
                              style={{ backgroundColor: v == null ? '#F3F4F6' : scoreColor(v), color: textFor(v) }}>
                              {v == null ? '–' : v}
                            </div>
                          </td>
                        );
                      })}
                      <td className="p-1 text-center font-bold" style={{ color: scoreTextColor(r.overall_avg) }}>
                        {r.overall_avg == null ? '–' : r.overall_avg}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center gap-2 mt-4 text-xs text-[#5C5C5C]">
                <span>Scale:</span>
                {[0, 1, 2, 3, 4, 5, 6].map((n) => (
                  <span key={n} className="w-6 h-5 rounded flex items-center justify-center font-semibold"
                    style={{ backgroundColor: scoreColor(n), color: textFor(n) }}>{n}</span>
                ))}
              </div>
            </div>
          )
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SkillMasteryModal;
